import { Currency } from "@types";
import { MemberBankFundingType } from "kz-ui-sdk";

/**
 * Valid PID type values that map to MemberUniqueInfoCollection enum
 */
export type PidType = "dni" | "ce" | "pen_pas" | "ruc" | "cc" | "nit" | "cop_pas";

/**
 * Form data interface for unique identifier fields
 */
export interface UniqueIdentifierFormData {
  pan?: string;
  cpf?: string;
  curp?: string;
  pidType?: PidType;
  pidNumber?: string;
}

/**
 * Gets translation key for unique identifier error messages
 * Returns keys like "pan is not valid", "cpf is not valid", etc.
 */
export function getUniqueIdentifierErrorKey(formData: UniqueIdentifierFormData): string {
  // Check direct fields first
  if (formData.pan) {
    return "pan is not valid";
  }
  if (formData.cpf) {
    return "cpf is not valid";
  }
  if (formData.curp) {
    return "curp is not valid";
  }

  // Check PID fields
  if (formData.pidType && formData.pidNumber) {
    switch (formData.pidType) {
      case "dni":
        return "dni is not valid";
      case "ce":
        return "ce is not valid";
      case "ruc":
        return "ruc is not valid";
      case "pen_pas":
        return "passport is not valid";
      case "cc":
        return "cc is not valid";
      case "nit":
        return "nit is not valid";
      case "cop_pas":
        return "passport is not valid";
      default:
        return "identifier is not valid";
    }
  }

  // Fallback
  return "identifier is not valid";
}

/**
 * Gets translation key for unique identifier duplicate error messages
 * Returns keys like "pan is duplicated", "cpf is duplicated", etc.
 */
export function getUniqueIdentifierDuplicateKey(formData: UniqueIdentifierFormData): string {
  // Check direct fields first
  if (formData.pan) {
    return "pan is duplicated";
  }
  if (formData.cpf) {
    return "cpf is duplicated";
  }
  if (formData.curp) {
    return "curp is duplicated";
  }

  // Check PID fields
  if (formData.pidType && formData.pidNumber) {
    switch (formData.pidType) {
      case "dni":
        return "dni is duplicated";
      case "ce":
        return "ce is duplicated";
      case "ruc":
        return "ruc is duplicated";
      case "pen_pas":
        return "passport is duplicated";
      case "cc":
        return "cc is duplicated";
      case "nit":
        return "nit is duplicated";
      case "cop_pas":
        return "passport is duplicated";
      default:
        return "identifier is duplicated";
    }
  }

  // Fallback
  return "identifier is duplicated";
}

/**
 * Gets translation key for bank account error messages
 * Returns keys like "account number is not valid", "upi id is not valid", "pix key is not valid", etc.
 */
export function getBankAccountErrorKey(currency: Currency, bankType?: MemberBankFundingType): string {
  switch (currency) {
    case Currency.INR:
      if (bankType === MemberBankFundingType.UPI) {
        return "upi id is not valid";
      }
      if (bankType === MemberBankFundingType.IFSC) {
        return "account number is not valid";
      }
      return "account number is not valid";
    case Currency.MXN:
      if (bankType === MemberBankFundingType.CLABE) {
        return "clabe is not valid";
      }
      return "account number is not valid";
    case Currency.BRL:
      if (bankType === MemberBankFundingType.PIX) {
        return "pix key is not valid";
      }
      return "account number is not valid";
    case Currency.PEN:
      if (bankType === MemberBankFundingType.CCI) {
        return "cci is not valid";
      }
      return "account number is not valid";
    default:
      return "account number is not valid";
  }
}

/**
 * Gets translation key for bank account duplicate error messages
 * Returns keys like "account number is duplicated", "upi id is duplicated", "pix key is duplicated", etc.
 */
export function getBankAccountDuplicateKey(currency: Currency, bankType?: MemberBankFundingType): string {
  switch (currency) {
    case Currency.INR:
      if (bankType === MemberBankFundingType.UPI) {
        return "upi id is duplicated";
      }
      if (bankType === MemberBankFundingType.IFSC) {
        return "account number is duplicated";
      }
      return "account number is duplicated";
    case Currency.MXN:
      if (bankType === MemberBankFundingType.CLABE) {
        return "clabe is duplicated";
      }
      return "account number is duplicated";
    case Currency.BRL:
      if (bankType === MemberBankFundingType.PIX) {
        return "pix key is duplicated";
      }
      return "account number is duplicated";
    case Currency.PEN:
      if (bankType === MemberBankFundingType.CCI) {
        return "cci is duplicated";
      }
      return "account number is duplicated";
    default:
      return "account number is duplicated";
  }
}
